// Dependencies
import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { Column } from '../objects/column';
import { PAYMEN_WAITING_COLUMN } from '../constants/columnsConstant';
// Types
import { ActivationDate } from '../../event/objects/activationDate';
import { Event } from '../../event/objects/event';
import { FeesStats } from '../objects/feesStats';
import { ManualAccommodationStatState } from '../objects/manualAccommodationStatState';
import { MarginStats, MarginStatsApi } from '../objects/marginStats';
import { OccupancyStats } from '../objects/occupancyStats';
import { Trip } from '../../trip/objects/trip';

const initialState: ManualAccommodationStatState = {
    edit_step: null,
    fees_stats: null,
    margin_stats: null,
    occupancy_stats: null,
    payment_waiting: [],
    payment_waiting_columns: PAYMEN_WAITING_COLUMN,
    running: false,
    selected_event: null,
    selected_year: null,
    client_list: [],
    client_list_loading: false,
    selected_client: null,
    room_products_list: [],
    room_products_list_filtered: [],
    room_products_list_loading: false,
    room_products_edit_actions: [],
    payment_waiting_list_loading: false,
    payment_waiting_current_version: null,
    payment_waiting_criteria: {
        for_user: null, order: 'asc', order_by: 'created-date', is_payment_waiting: true, society: '',
        due_date__lte: null,
        due_date__gte: null,
        total_cost__lte: '',
        total_cost__gte: '',
        total_paid__gte: '',
        total_paid__lte: '',
        balance__gte: '',
        balance__lte: '',
        current_version__payment_type: '',
        current_version__payment_mode: ''
    },
    margin_search: '',
    payment_waiting_search: '',
    margin_loading: false,
    vat_credential: { start_date: null, end_date: null },
    paiment_waiting_refresh: false
};

export const manualAccommodationStatSlice = createSlice({
    name: 'manual_accommodation_stat',
    initialState,
    reducers: {
        setEditStep: (state, action: PayloadAction<null | 'MARGIN_STATS' | 'OCCUPANCY_STATS' | 'FEES_STATS' | 'PAYMENT_WAITING' | 'WAINTING_FOR_TREATMENT' | 'EXTRA_HANDLE' | 'NIGHT_REQUEST'>) => {
            state.edit_step = action.payload;
            state.margin_search = '';
            state.payment_waiting_search = '';
        },
        setFeesStats: (state, action: PayloadAction<null | FeesStats>) => {
            state.fees_stats = action.payload;
        },
        setMarginStats: (state, action: PayloadAction<MarginStatsApi>) => {
            const row: MarginStats = [];
            Object.keys(action.payload?.hotel).forEach((hotel_key, hotel_index) => {
                Object.keys(action.payload?.hotel[hotel_key].room).forEach((room_key, room_index) => {
                    action.payload?.hotel[hotel_key].room[room_key].prices.map((price, price_index) => {
                        row.push({
                            hotel_name: action.payload?.hotel[hotel_key].hotel_name,
                            room_name: room_key,
                            ...price
                        });
                    });
                });
            });
            state.margin_stats = row;
        },
        resetMarginStats: (state) => ({
            ...state, margin_stats: []
        }),
        setOccupancyStats: (state, action: PayloadAction<null | OccupancyStats>) => {
            state.occupancy_stats = action.payload;
        },
        setPaymentWaitingList: (state, action) => {
            const results = action.payload.results;
            const filtered_results: any[] = [];
            results.map((trip: any) => {
                let already_paid = 0;
                trip.payments_done.map((payment: any) => {
                    already_paid += parseFloat(payment.amount);
                });
                if (already_paid < parseFloat(trip.current_version.total_cost)) {
                    filtered_results.push({ ...trip });
                }
            });
            state.payment_waiting = filtered_results;
            state.payment_waiting_list_loading = false;
        },
        setClientList: (state, action) => ({
            ...state, client_list: action.payload, client_list_loading: false
        }),
        setClientListLoading: (state, action) => ({
            ...state, client_list_loading: action.payload
        }),
        setRoomProductsList: (state, action) => ({
            ...state, room_products_list: action.payload, room_products_list_loading: false
        }),
        setRoomProductsListItem: (state, action) => ({
            ...state, room_products_list: state.room_products_list.map((state) => state.id === action.payload.id ? action.payload : state)
        }),
        setRoomProductsListFiltered: (state, action) => ({
            ...state, room_products_list_filtered: action.payload
        }),
        setRoomProductsListLoading: (state, action) => ({
            ...state, room_products_list_loading: action.payload
        }),
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        updateSelectedEvent: (state, action: PayloadAction<null | Event>) => {
            state.selected_event = action.payload;
            state.selected_year = null;
            state.margin_stats = null;
            state.occupancy_stats = null;
            state.fees_stats = null;
            state.payment_waiting = [];
            state.room_products_list = [];
            state.edit_step = action.payload ? state.edit_step : null;
        },
        updateSelectedYear: (state, action: PayloadAction<null | ActivationDate>) => {
            const selected_year = state.selected_event?.activation_dates.find(dates => dates.id === action.payload);
            state.selected_year = selected_year ? selected_year : null;
        },
        setSelectedClientList: (state, action) => ({
            ...state, selected_client: action.payload, room_products_list: action.payload ? state.room_products_list : []
        }),
        localUpdateforRoomProduct: (state, action) => ({
            ...state, room_products_list: state.room_products_list.map((room_product) => action.payload.room_products.includes(room_product.id) ? { ...room_product, booking_status: { ...room_product.booking_status, status_booking: action.payload.status }, modified_date: new Date() } : room_product)
        }),
        setRoomProductsEditActions: (state, action) => ({
            ...state, room_products_edit_actions: action.payload
        }),
        setPaymentWaitingListLoading: (state, action) => ({
            ...state, payment_waiting_list_loading: action.payload
        }),
        updatePaymentWaitingCriteria: (state, action) => ({
            ...state, payment_waiting_criteria: action.payload
        }),
        setMarginSearch: (state, action) => ({
            ...state, margin_search: action.payload
        }),
        setPaymentWaitingSearch: (state, action) => ({
            ...state, payment_waiting_search: action.payload
        }),
        setMarginLoading: (state, action) => ({
            ...state, margin_loading: action.payload
        }),
        setVatCredential: (state, action) => ({
            ...state, vat_credential: action.payload
        }),
        setPaimentWaitingCurrentVersion: (state, action) => ({
            ...state, payment_waiting_current_version: action.payload
        }),
        togglePaimentWaitingRefresh: (state) => ({
            ...state, paiment_waiting_refresh: !state.paiment_waiting_refresh
        }),
        resizePaymentWaitingColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.payment_waiting_columns = applyColumnResize(state.payment_waiting_columns, action.payload);
        }
    },
});

export const {
    setEditStep,
    setFeesStats,
    setMarginStats,
    setOccupancyStats,
    setPaymentWaitingList,
    setRunning,
    updateSelectedEvent,
    updateSelectedYear,
    setClientList,
    setClientListLoading,
    setRoomProductsList,
    setRoomProductsListFiltered,
    setRoomProductsListLoading,
    setSelectedClientList,
    localUpdateforRoomProduct,
    setRoomProductsEditActions,
    setPaymentWaitingListLoading,
    updatePaymentWaitingCriteria,
    setMarginSearch,
    setPaymentWaitingSearch,
    setMarginLoading,
    resetMarginStats,
    setVatCredential,
    setPaimentWaitingCurrentVersion,
    togglePaimentWaitingRefresh,
    resizePaymentWaitingColumn
} = manualAccommodationStatSlice.actions;

export default manualAccommodationStatSlice.reducer;
